<template>
<div
    v-if="visible"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4"
>
    <div class="bg-white rounded-lg w-full max-w-3xl px-6 py-12 md:p-12 space-y-4">
        <!-- ヘッダー -->
        <div class="flex justify-between items-center pb-4 border-b">
            <h3 class="text-xl font-bold">リンクカード</h3>
            <button class="link-styles--gray text-2xl" @click="emit('close')">
                <i class="fa-light fa-xmark" title="Close"></i>
            </button>
        </div>

        <!-- レイアウト: SP=1カラム縦積み / PC=2カラム横並び -->
        <div class="flex flex-col md:flex-row gap-8">
            <!-- フォーム -->
            <div class="flex-1 min-w-0">
                <!-- リンク先URL -->
                <div class="pb-6">
                    <label class="block text-sm font-bold mb-2">
                        リンク先URL <span class="text-alert text-xs">必須</span>
                    </label>
                    <input
                        v-model="form.url"
                        @input="validateUrl"
                        type="text"
                        placeholder="https://example.com"
                        class="placeholder-placeholder w-full p-2 border rounded-sm"
                        :class="{ 'border-alert': errors.url }"
                    />
                    <p v-if="errors.url" class="text-alert text-sm font-bold mt-1">{{ errors.url }}</p>
                </div>

                <!-- カード見出し -->
                <div class="pb-2">
                    <label class="block text-sm font-bold mb-2">
                        カード見出し <span class="text-alert text-xs">必須</span>
                    </label>
                    <input
                        v-model="form.title"
                        @input="validateTitle"
                        type="text"
                        placeholder="カード見出しを入力してください"
                        class="placeholder-placeholder w-full p-2 border rounded-sm"
                        :class="{ 'border-alert': errors.title }"
                    />
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="titleCounterClass">
                            {{ form.title.length }}/{{ TITLE_MAX_LENGTH }}
                        </span>
                    </div>
                    <p v-if="errors.title" class="text-alert text-sm font-bold">{{ errors.title }}</p>
                </div>

                <!-- カード本文 -->
                <div class="pb-2">
                    <label class="block text-sm font-bold mb-2">
                        カード本文 <span class="text-alert text-xs">必須</span>
                    </label>
                    <textarea
                        v-model="form.description"
                        @input="validateDescription"
                        placeholder="カード本文を入力してください"
                        class="placeholder-placeholder w-full p-2 border rounded-sm h-24 resize-y"
                        :class="{ 'border-alert': errors.description }"
                    ></textarea>
                    <div class="flex justify-end mt-1">
                        <span class="text-xs" :class="descriptionCounterClass">
                            {{ form.description.length }}/{{ DESCRIPTION_MAX_LENGTH }}
                        </span>
                    </div>
                    <p v-if="errors.description" class="text-alert text-sm font-bold">{{ errors.description }}</p>
                </div>

                <!-- アフィリエイト -->
                <div class="pb-6">
                    <label class="block text-sm font-bold mb-2">アフィリエイト</label>
                    <div class="flex items-center bg-base px-3 py-2">
                        <input
                            v-model="form.is_affiliate"
                            type="checkbox"
                            id="is_affiliate"
                            class="mr-2"
                        />
                        <label for="is_affiliate" class="text-main">アフィリエイトリンクを含む</label>
                    </div>
                    <p class="text-sub text-xs mt-2">アフィリエイトリンクを含む場合は、読者に開示する必要があります。チェックをオンにすると、リンクカード内にPR表記が表示されます。</p>
                </div>
            </div>

            <!-- 画像 -->
            <div class="w-full md:w-60 md:shrink-0">
                <label class="block text-sm font-bold mb-2">画像</label>

                <!-- プレビューあり -->
                <div v-if="imagePreviewUrl">
                    <img :src="imagePreviewUrl" alt="プレビュー" class="w-full max-h-96 rounded-sm object-contain bg-gray-100" />
                    <div class="flex items-center justify-between bg-gray-100 rounded-sm p-4 mt-0">
                        <div class="min-w-0">
                            <p class="text-sub text-xs">ファイル名</p>
                            <p class="text-sm truncate">{{ imageDisplayName }}</p>
                        </div>
                        <button class="text-alert text-lg shrink-0 ml-2" @click="removeImage">
                            <i class="fa-light fa-xmark"></i>
                        </button>
                    </div>
                </div>

                <!-- アップロードエリア -->
                <div
                    v-else
                    class="border-2 border-dashed border-placeholder rounded-sm p-6 text-center"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleDrop"
                    :class="{ 'border-blue-400 bg-blue-50': isDragging }"
                >
                    <!-- 上矢印アイコン (Figma準拠) -->
                    <div class="flex justify-center mb-3">
                        <div class="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                            <i class="fa-light fa-arrow-up text-2xl text-sub"></i>
                        </div>
                    </div>
                    <p class="text-main text-sm font-bold mb-2 leading-6">
                        画像ファイルを<br />ドラッグ＆ドロップ
                    </p>
                    <p class="text-sub text-sm mb-3">または</p>
                    <button class="btn-secondary text-sm" @click="fileInputRef.click()">
                        ファイルを選択
                    </button>
                    <input
                        ref="fileInputRef"
                        type="file"
                        accept="image/png,image/jpeg"
                        class="hidden"
                        @change="handleFileSelect"
                    />
                    <ul class="text-tertiary text-xs mt-4 text-left list-disc list-inside">
                        <li>1ファイルの容量は10MBまで</li>
                        <li>png、jpgファイルに対応</li>
                    </ul>
                </div>
                <p v-if="errors.image" class="text-alert text-xs mt-1">{{ errors.image }}</p>
            </div>
        </div>

        <!-- 送信ボタン -->
        <div class="border-t pt-4 flex justify-end">
            <button
                @click="handleSubmit"
                :disabled="!isFormValid || isSaving"
                class="w-full md:w-auto"
                :class="isFormValid && !isSaving ? 'btn-primary' : 'btn-disabled'"
            >
                <span v-if="isSaving">
                    <i class="fa-solid fa-spinner fa-spin mr-1"></i> 保存中...
                </span>
                <span v-else>{{ isEditMode ? 'カードを更新する' : 'カードを追加する' }}</span>
            </button>
        </div>
    </div>
</div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const TITLE_MAX_LENGTH = 40;
const DESCRIPTION_MAX_LENGTH = 120;
const URL_MAX_LENGTH = 2048;

/**
 * 改行コードを LF に正規化する（防御的処理）。
 * 主な CRLF 混入経路:
 *   - 過去バージョンで <form> ネイティブ送信により CRLF 保存されたレコード
 *   - サーバー正規化以前の投稿分（レガシーデータ）
 * フロントの `String.length` は LF 前提でカウントするため、editData ロード時点で LF に統一する。
 */
const normalizeNewlines = (value) => {
    if (typeof value !== 'string') return value;
    return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
};

/**
 * URL 形式のバリデーション。http/https スキーム + host 必須。
 * new URL() でパースを試み、スキームと host の存在をチェックする。
 */
const isValidUrl = (value) => {
    try {
        const u = new URL(value);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        if (!u.hostname) return false;
        // hostname にドットを含むか、localhost/IPアドレスを許可
        // "http://a" のような単一トークンhostは弾く
        if (!u.hostname.includes('.') && u.hostname !== 'localhost') return false;
        return true;
    } catch {
        return false;
    }
};

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    articleId: {
        type: [Number, String],
        required: true,
    },
    editData: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;

const form = reactive({
    url: '',
    title: '',
    description: '',
    is_affiliate: false,
});
const errors = reactive({});
const imageFile = ref(null);
const imagePreviewUrl = ref(null);
const imageDisplayName = ref('');
const removeExistingImage = ref(false);
const isDragging = ref(false);
const isSaving = ref(false);
const fileInputRef = ref(null);

const isEditMode = computed(() => props.editData && props.editData.id);

const isFormValid = computed(() => {
    const trimmedUrl = form.url.trim();
    return (
        trimmedUrl !== '' &&
        trimmedUrl.length <= URL_MAX_LENGTH &&
        isValidUrl(trimmedUrl) &&
        form.title.trim() !== '' &&
        form.title.length <= TITLE_MAX_LENGTH &&
        form.description.trim() !== '' &&
        form.description.length <= DESCRIPTION_MAX_LENGTH &&
        !errors.image
    );
});

const titleCounterClass = computed(() => {
    if (form.title.length > TITLE_MAX_LENGTH) return 'text-alert';
    if (form.title.length >= TITLE_MAX_LENGTH - 5) return 'text-yellow-600';
    return 'text-sub';
});

const descriptionCounterClass = computed(() => {
    if (form.description.length > DESCRIPTION_MAX_LENGTH) return 'text-alert';
    if (form.description.length >= DESCRIPTION_MAX_LENGTH - 20) return 'text-yellow-600';
    return 'text-sub';
});

const resetForm = () => {
    form.url = '';
    form.title = '';
    form.description = '';
    form.is_affiliate = false;
    imageFile.value = null;
    imagePreviewUrl.value = null;
    imageDisplayName.value = '';
    removeExistingImage.value = false;
    isSaving.value = false;
    // errors をクリア
    Object.keys(errors).forEach(key => delete errors[key]);
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        resetForm();
        if (props.editData) {
            form.url = props.editData.url || '';
            form.title = props.editData.title || '';
            // 既存レコードの CRLF を LF に正規化してからセット（length 一致のため）
            form.description = normalizeNewlines(props.editData.description || '');
            form.is_affiliate = props.editData.is_affiliate || false;
            if (props.editData.image) {
                imagePreviewUrl.value = props.editData.image;
                imageDisplayName.value = props.editData.image_filename || 'image';
            }
        }
    }
});

const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) processFile(file);
};

const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer.files[0];
    if (file) processFile(file);
};

const processFile = (file) => {
    delete errors.image;

    if (!ALLOWED_TYPES.includes(file.type)) {
        errors.image = 'png、jpgファイルのみ対応しています';
        return;
    }
    if (file.size > MAX_FILE_SIZE) {
        errors.image = '画像は10MB以内にしてください';
        return;
    }

    imageFile.value = file;
    imagePreviewUrl.value = URL.createObjectURL(file);
    imageDisplayName.value = file.name;
    removeExistingImage.value = false;
};

const removeImage = () => {
    if (imagePreviewUrl.value && imageFile.value) {
        URL.revokeObjectURL(imagePreviewUrl.value);
    }
    imageFile.value = null;
    imagePreviewUrl.value = null;
    imageDisplayName.value = '';
    // 編集モードで既存画像を削除する場合
    if (isEditMode.value && props.editData.image) {
        removeExistingImage.value = true;
    }
};

/**
 * 個別バリデーション。@input からリアルタイムで呼ばれる。
 * 未入力(空)の場合はエラー表示しない（初期表示時のノイズ防止）。
 */
const validateUrl = () => {
    delete errors.url;
    const trimmedUrl = form.url.trim();
    if (!trimmedUrl) return;
    if (trimmedUrl.length > URL_MAX_LENGTH) {
        errors.url = `リンク先URLは${URL_MAX_LENGTH}文字以内にしてください`;
    } else if (!isValidUrl(trimmedUrl)) {
        errors.url = 'http:// または https:// で始まる有効なURLを入力してください';
    }
};

const validateTitle = () => {
    delete errors.title;
    if (form.title.length > TITLE_MAX_LENGTH) {
        errors.title = `カード見出しは${TITLE_MAX_LENGTH}文字以内にしてください`;
    }
};

const validateDescription = () => {
    delete errors.description;
    if (form.description.length > DESCRIPTION_MAX_LENGTH) {
        errors.description = `カード本文は${DESCRIPTION_MAX_LENGTH}文字以内にしてください`;
    }
};

/**
 * 送信直前の全体バリデーション。未入力の場合もエラー表示する。
 */
const validate = () => {
    // errors をクリア
    Object.keys(errors).forEach(key => delete errors[key]);

    const trimmedUrl = form.url.trim();
    if (!trimmedUrl) {
        errors.url = 'リンク先URLは必須です';
    } else if (trimmedUrl.length > URL_MAX_LENGTH) {
        errors.url = `リンク先URLは${URL_MAX_LENGTH}文字以内にしてください`;
    } else if (!isValidUrl(trimmedUrl)) {
        errors.url = 'http:// または https:// で始まる有効なURLを入力してください';
    }
    if (!form.title.trim()) {
        errors.title = 'カード見出しは必須です';
    } else if (form.title.length > TITLE_MAX_LENGTH) {
        errors.title = `カード見出しは${TITLE_MAX_LENGTH}文字以内にしてください`;
    }
    if (!form.description.trim()) {
        errors.description = 'カード本文は必須です';
    } else if (form.description.length > DESCRIPTION_MAX_LENGTH) {
        errors.description = `カード本文は${DESCRIPTION_MAX_LENGTH}文字以内にしてください`;
    }
    return Object.keys(errors).length === 0;
};

const handleSubmit = async () => {
    if (!validate()) return;

    isSaving.value = true;
    try {
        const accessToken = store.state.accessToken;

        const formData = new FormData();
        formData.append('url', form.url.trim());
        formData.append('title', form.title.trim());
        formData.append('description', form.description.trim());
        formData.append('is_affiliate', form.is_affiliate);

        if (imageFile.value) {
            formData.append('image', imageFile.value);
        }
        if (removeExistingImage.value) {
            formData.append('remove_image', 'true');
        }

        const headers = {
            Authorization: `Bearer ${accessToken}`,
        };

        let response;
        if (isEditMode.value) {
            response = await axios.put(
                `${apiEndpoint}/articles/${props.articleId}/link-cards/${props.editData.id}/`,
                formData,
                { headers }
            );
        } else {
            response = await axios.post(
                `${apiEndpoint}/articles/${props.articleId}/link-cards/`,
                formData,
                { headers }
            );
        }

        emit('saved', response.data);
    } catch (error) {
        console.error('リンクカード保存に失敗しました:', error);
        if (error.response?.data?.error) {
            alert(error.response.data.error);
        } else {
            alert('リンクカードの保存に失敗しました。');
        }
    } finally {
        isSaving.value = false;
    }
};

onBeforeUnmount(() => {
    // ObjectURL のクリーンアップ
    if (imagePreviewUrl.value && imageFile.value) {
        URL.revokeObjectURL(imagePreviewUrl.value);
    }
});
</script>
